import firebase from "../firebase";
import {
        collection,
        doc,
        getDoc,
        getDocs,
        setDoc,
        updateDoc,
        deleteDoc,
        query,
        where,
        orderBy,
        limit,
        startAfter,
        getFirestore,
} from "firebase/firestore";
import {
        createCacheKey,
        fetchWithCache,
        invalidateCache,
} from "../utils/firestoreCache";

// Get the Firestore database instance
const db = getFirestore(firebase);

const WORK_ITEM_CACHE_NAMESPACE = "workItems";
const WORK_ITEM_CACHE_DEFAULT_MS = 45_000;

const invalidateWorkItemCache = (userId) => {
        if (!userId) {
                return;
        }

        invalidateCache(`${WORK_ITEM_CACHE_NAMESPACE}:${userId}`);
};

const extractWorkItemCacheOptions = (options) => {
        if (typeof options === "boolean") {
                return { forceRefresh: options, cacheDurationMs: undefined, queryOptions: {} };
        }

        if (!options || typeof options !== "object") {
                return { forceRefresh: false, cacheDurationMs: undefined, queryOptions: {} };
        }

        const { forceRefresh = false, cacheDurationMs, ...queryOptions } = options;

        return {
                forceRefresh: Boolean(forceRefresh),
                cacheDurationMs:
                        typeof cacheDurationMs === "number" && !Number.isNaN(cacheDurationMs)
                                ? cacheDurationMs
                                : undefined,
                queryOptions,
        };
};

/**
 * Work Planner API for managing work tasks in Firestore
 *
 * Collection structure based on actual Firestore database:
 * - Users/{userId}/WorkHistory - Collection of work items for a user (note capital 'U')
 *   - task_{timestamp}_{id} - Document containing work item data
 *     - title: string - Title of the work item (e.g., "testtask")
 *     - t: number - Start timestamp in milliseconds
 *     - endTime: number - End timestamp in milliseconds
 *     - location: string - Location where the task is performed (e.g., "Büro")
 *     - notes: string - Additional information about the task
 *     - assignedTo: object - Worker assigned to the task
 *       - id: string - Worker ID (FCM token)
 *       - isActive: boolean - Whether the worker is active
 *       - lastActivity: null|number - Last activity timestamp
 *       - name: string - Name of the worker (e.g., "Mooooo")
 *       - token: string - FCM token for notifications
 *     - assignedToName: string - Name of the assigned worker (e.g., "Mooooo")
 *     - status: string - Current status (e.g., "pending")
 *     - type: string - Type of task ("Work", "Health", "Treatment")
 *     - createdAt: number - Timestamp when created
 *     - updatedAt: number - Timestamp when updated
 */

/**
 * Get all work items for a user with optional filtering
 * @param {string} userId - The user ID
 * @param {Object} options - Query options
 * @param {Date} [options.startDate] - Filter for items after this date
 * @param {Date} [options.endDate] - Filter for items before this date
 * @param {Array<string>} [options.statuses] - Filter by these statuses
 * @param {Array<string>} [options.types] - Filter by these types
 * @param {number} [options.limit] - Limit the number of results
 * @param {string} [options.lastDocId] - Last document ID for pagination
 * @param {boolean} [options.taskPrefixOnly] - Only return items with IDs starting with 'task_'
 * @returns {Promise<Array>} - Promise resolving to array of work items
 */
export const getWorkItems = async (userId, options = {}) => {
        const { forceRefresh, cacheDurationMs, queryOptions } = extractWorkItemCacheOptions(options);

        const cacheKey = createCacheKey(
                WORK_ITEM_CACHE_NAMESPACE,
                userId,
                queryOptions
        );

        return fetchWithCache(
                cacheKey,
                async () => {
                        try {
                                const workItemsRef = collection(db, `Users/${userId}/WorkHistory`);

                                // Build query with filters
                                const queryConstraints = [];

                                // Date range filtering
                                if (queryOptions.startDate instanceof Date) {
                                        queryConstraints.push(
                                                where("t", ">=", queryOptions.startDate.getTime())
                                        );
                                }

                                if (queryOptions.endDate instanceof Date) {
                                        queryConstraints.push(
                                                where("t", "<=", queryOptions.endDate.getTime())
                                        );
                                }

                                // Status filtering - only apply to task_ items
                                if (
                                        queryOptions.statuses &&
                                        queryOptions.statuses.length > 0 &&
                                        queryOptions.statuses.length < 4 &&
                                        queryOptions.taskPrefixOnly
                                ) {
                                        queryConstraints.push(where("status", "in", queryOptions.statuses));
                                }

                                // Type filtering - only apply to task_ items
                                if (
                                        queryOptions.types &&
                                        queryOptions.types.length > 0 &&
                                        queryOptions.types.length < 4 &&
                                        queryOptions.taskPrefixOnly
                                ) {
                                        queryConstraints.push(where("type", "in", queryOptions.types));
                                }

                                // Always order by start time for consistency
                                queryConstraints.push(orderBy("t", "desc"));

                                // Pagination
                                if (queryOptions.limit) {
                                        queryConstraints.push(limit(queryOptions.limit));
                                }

                                // Start after for pagination
                                if (queryOptions.lastDocId) {
                                        const lastDocRef = doc(
                                                db,
                                                `Users/${userId}/WorkHistory`,
                                                queryOptions.lastDocId
                                        );
                                        const lastDocSnap = await getDoc(lastDocRef);
                                        if (lastDocSnap.exists()) {
                                                queryConstraints.push(startAfter(lastDocSnap));
                                        }
                                }

                                // Execute query
                                const q = query(workItemsRef, ...queryConstraints);
                                const snapshot = await getDocs(q);

                                // Process results
                                let results = snapshot.docs.map((docSnap) => {
                                        const data = docSnap.data();
                                        const isTaskPrefixed = docSnap.id.startsWith("task_");

                                        const workItem = {
                                                id: docSnap.id,
                                                // Convert timestamp numbers to JavaScript Date objects for UI
                                                startDate: data.t ? new Date(data.t) : null,
                                                endDate: data.endTime ? new Date(data.endTime) : null,
                                                createdAt: data.createdAt ? new Date(data.createdAt) : null,
                                                updatedAt: data.updatedAt ? new Date(data.updatedAt) : null,
                                                // Keep original fields for compatibility with existing code
                                                t: data.t,
                                                endTime: data.endTime,
                                        };

                                        if (isTaskPrefixed) {
                                                Object.assign(workItem, {
                                                        title: data.title || "",
                                                        location: data.location || "",
                                                        notes: data.notes || "",
                                                        type: data.type || "Work",
                                                        status: data.status || "pending",
                                                        assignedTo: data.assignedTo || null,
                                                        assignedToName: data.assignedToName || "",
                                                        SowID: data.SowID || "",
                                                        selectedGroup: data.selectedGroup || "",
                                                });
                                        } else {
                                                Object.assign(workItem, {
                                                        title: data.CustomName || data.WorkItemType || "Untitled",
                                                        type: data.WorkItemType || "General",
                                                        status: "completed",
                                                        SowID: data.SowID || "",
                                                        selectedGroup: data.Group || "",
                                                        notes: data.Notes || data.Comment || "",
                                                        ...data,
                                                });
                                        }

                                        return workItem;
                                });

                                if (queryOptions.taskPrefixOnly) {
                                        results = results.filter((item) => item.id.startsWith("task_"));
                                }

                                return results;
                        } catch (error) {
                                console.error("Error getting work items:", error);
                                throw new Error(`Failed to get work items: ${error.message}`);
                        }
                },
                {
                        ttlMs: cacheDurationMs ?? WORK_ITEM_CACHE_DEFAULT_MS,
                        forceRefresh,
                }
        );
};

/**
 * Get a specific work item by ID
 * @param {string} userId - The user ID
 * @param {string} workItemId - The work item ID (task_{timestamp}_{id} format)
 * @returns {Promise<Object>} - Promise resolving to work item object
 */
export const getWorkItem = async (userId, workItemId) => {
	try {
		const workItemRef = doc(db, `Users/${userId}/WorkHistory`, workItemId);
		const snapshot = await getDoc(workItemRef);

		if (!snapshot.exists()) {
			throw new Error(`Work item not found: ${workItemId}`);
		}

		const data = snapshot.data();

		// Create the return object with transformed dates and all possible fields
		return {
			id: snapshot.id,
			// Convert timestamp numbers to JavaScript Date objects
			startDate: data.t ? new Date(data.t) : null,
			endDate: data.endTime ? new Date(data.endTime) : null,
			createdAt: data.createdAt ? new Date(data.createdAt) : null,
			updatedAt: data.updatedAt ? new Date(data.updatedAt) : null,
			// Keep original fields for compatibility
			t: data.t,
			endTime: data.endTime,
			// Include other fields from data
			title: data.title || "",
			location: data.location || "",
			notes: data.notes || "",
			type: data.type || "Work",
			status: data.status || "pending",
			assignedTo: data.assignedTo || null,
			assignedToName: data.assignedToName || "",
			SowID: data.SowID || "",
			selectedGroup: data.selectedGroup || "",
		};
	} catch (error) {
		console.error("Error getting work item:", error);
		throw new Error(`Failed to get work item: ${error.message}`);
	}
};

/**
 * Create or update a work item
 * @param {string} userId - The user ID
 * @param {Object} workItemData - The work item data
 * @param {string} [workItemId] - Optional work item ID (for updates)
 * @returns {Promise<string>} - Promise resolving to work item ID
 */
export const saveWorkItem = async (userId, workItemData, workItemId = null) => {
	try {
		// Validate required parameters
		if (!userId) {
			throw new Error("User ID is required");
		}

		if (!workItemData || typeof workItemData !== "object") {
			throw new Error("Work item data must be a valid object");
		}

		const workItemsRef = collection(db, `Users/${userId}/WorkHistory`);
		const timestamp = Date.now();

		// Normalize and parse dates
		const normalizeDate = (dateValue, defaultValue) => {
			if (dateValue instanceof Date) {
				return dateValue.getTime();
			} else if (typeof dateValue === "number") {
				return dateValue;
			} else if (typeof dateValue === "string") {
				const parsedDate = new Date(dateValue);
				return isNaN(parsedDate.getTime())
					? defaultValue
					: parsedDate.getTime();
			}
			return defaultValue;
		};

		// Create a properly structured object for Firestore
		const firestoreData = {
			// Basic fields - use empty strings for text fields to avoid null/undefined issues
			title: workItemData.title || "",
			location: workItemData.location || "",
			notes: workItemData.notes || "",
			type: workItemData.type || "Work",
			status: workItemData.status || "pending",

			// Date fields
			t: normalizeDate(workItemData.startDate, timestamp),
			endTime: normalizeDate(workItemData.endDate, timestamp + 86400000), // Default to 24 hours later

			// Animal ID and Group fields
			SowID: workItemData.SowID || workItemData.animalId || "",
			selectedGroup: workItemData.selectedGroup || "",

			// Additional fields if present in workItemData
			priority: workItemData.priority || "medium",
			diagnoseCategory: workItemData.diagnoseCategory || null,

			// Timestamps
			updatedAt: timestamp,
		};

		// Worker assignment if present - handle various possible formats consistently
		if (workItemData.assignedTo) {
			// Extract worker ID from different possible formats
			const workerId =
				typeof workItemData.assignedTo === "object"
					? workItemData.assignedTo.id || workItemData.assignedTo.token || ""
					: workItemData.assignedTo;

			// Extract worker name
			const workerName =
				workItemData.assignedToName ||
				(workItemData.assignedTo && workItemData.assignedTo.name) ||
				"";

			// Create a consistent worker object
			firestoreData.assignedTo = {
				id: workerId,
				token: workerId, // Use ID as token for consistency
				name: workerName,
				isActive: true,
				lastActivity: timestamp,
			};

			firestoreData.assignedToName = workerName;
		}

		// Handle optional fields
		if (workItemData.devices && Array.isArray(workItemData.devices)) {
			firestoreData.devices = workItemData.devices;
		}

                // If creating a new item
                if (!workItemId) {
			// Generate ID with timestamp pattern matching Firestore: task_{timestamp}_{id}
			const newId = `task_${timestamp}_${Math.floor(Math.random() * 10000)}`;
			const newItemRef = doc(workItemsRef, newId);

			// Add creation timestamp
			firestoreData.createdAt = timestamp;

			// Perform the write operation
                        await setDoc(newItemRef, firestoreData);
                        invalidateWorkItemCache(userId);
                        return newId;
                }
                // If updating existing item
                else {
			const workItemRef = doc(db, `Users/${userId}/WorkHistory`, workItemId);

			// Verify the document exists before updating
			const docSnap = await getDoc(workItemRef);
			if (!docSnap.exists()) {
				throw new Error(`Work item with ID ${workItemId} does not exist`);
			}

			// Perform the update operation
                        await updateDoc(workItemRef, firestoreData);
                        invalidateWorkItemCache(userId);
                        return workItemId;
                }
	} catch (error) {
		console.error("Error saving work item:", error);
		// Add more context to the error
		throw new Error(`Failed to save work item: ${error.message}`);
	}
};

/**
 * Update the status of a work item
 * @param {string} userId - The user ID
 * @param {string} workItemId - The work item ID
 * @param {string} status - The new status (pending, in-progress, completed, cancelled)
 * @returns {Promise<void>}
 */
export const updateWorkItemStatus = async (userId, workItemId, status) => {
	try {
		if (
			!["pending", "in-progress", "completed", "cancelled"].includes(status)
		) {
			throw new Error(`Invalid status: ${status}`);
		}

		const workItemRef = doc(db, `Users/${userId}/WorkHistory`, workItemId);
                await updateDoc(workItemRef, {
                        status,
                        updatedAt: Date.now(),
                });
                invalidateWorkItemCache(userId);
	} catch (error) {
		console.error("Error updating work item status:", error);
		throw new Error(`Failed to update work item status: ${error.message}`);
	}
};

/**
 * Delete a work item
 * @param {string} userId - The user ID
 * @param {string} workItemId - The work item ID
 * @returns {Promise<void>}
 */
export const deleteWorkItem = async (userId, workItemId) => {
	try {
		const workItemRef = doc(db, `Users/${userId}/WorkHistory`, workItemId);
                await deleteDoc(workItemRef);
                invalidateWorkItemCache(userId);
	} catch (error) {
		console.error("Error deleting work item:", error);
		throw new Error(`Failed to delete work item: ${error.message}`);
	}
};

/**
 * Get work items filtered by devices
 * @param {string} userId - The user ID
 * @param {Array<Object>} devices - Array of device objects with Token property
 * @returns {Promise<Array>} - Promise resolving to array of filtered work items
 */
export const getWorkItemsByDevices = async (userId, devices, options = {}) => {
        try {
                if (!devices || !Array.isArray(devices) || devices.length === 0) {
                        return await getWorkItems(userId, options);
                }

                // Extract tokens from device objects
                const deviceTokens = devices.map((device) => device.Token);

                // Get all items - we'll filter client-side for now
                // For large datasets, consider implementing a more efficient server-side query
                const allWorkItems = await getWorkItems(userId, options);

		return allWorkItems.filter((item) => {
			if (
				!item.devices ||
				!Array.isArray(item.devices) ||
				item.devices.length === 0
			) {
				return false;
			}
			return item.devices.some((deviceId) => deviceTokens.includes(deviceId));
		});
	} catch (error) {
		console.error("Error getting work items by devices:", error);
		throw new Error(`Failed to get work items by devices: ${error.message}`);
	}
};

/**
 * Get count of work items by status
 * @param {string} userId - The user ID
 * @returns {Promise<Object>} - Promise resolving to counts by status
 */
export const getWorkItemCounts = async (userId) => {
	try {
		const workItems = await getWorkItems(userId);

		// Only count Work type tasks
		const workTasks = workItems.filter((task) => task.type === "Work");

		return {
			total: workTasks.length,
			pending: workTasks.filter(
				(task) => !task.status || task.status === "pending"
			).length,
			inProgress: workTasks.filter((task) => task.status === "in-progress")
				.length,
			completed: workTasks.filter((task) => task.status === "completed").length,
			cancelled: workTasks.filter((task) => task.status === "cancelled").length,
		};
	} catch (error) {
		console.error("Error getting work item counts:", error);
		throw new Error(`Failed to get work item counts: ${error.message}`);
	}
};

/**
 * Get work items for a specific animal
 * @param {string} userId - The user ID
 * @param {string} animalId - The animal ID to filter by
 * @param {Object} options - Additional query options
 * @returns {Promise<Array>} - Promise resolving to array of work items for the animal
 */
export const getAnimalWorkItems = async (userId, animalId, options = {}) => {
	try {
                // Get all work items (both task_ and non-task_ prefixed)
                const allWorkItems = await getWorkItems(userId, {
                        ...options,
			// Don't use taskPrefixOnly to get all items
		});

		// Filter for this specific animal's items
		const animalWorkItems = allWorkItems.filter((item) => {
			// Match against SowID field from either type of item
			const isMatch =
				item.SowID && item.SowID.toString() === animalId.toString();
			
                        return isMatch;
                });

                return animalWorkItems;
	} catch (error) {
		console.error(`Error getting work items for animal ${animalId}:`, error);
		throw new Error(`Failed to get work items for animal: ${error.message}`);
	}
};
